import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import {
  initializeMsal,
  isAuthenticated as checkAuth,
  login as msalLogin,
  logout as msalLogout,
  acquireToken as msalAcquireToken,
  acquireTokenForGraph as msalAcquireTokenForGraph,
  acquireTokenForOpenAI as msalAcquireTokenForOpenAI,
  forceLogin as msalForceLogin,
  getUserName,
  getUserEmail,
} from "../auth";
import { useConfig } from "./ConfigContext";

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  userName: string | null;
  userEmail: string | null;
  error: string | null;
}

interface AuthActions {
  login: () => Promise<void>;
  logout: () => Promise<void>;
  acquireToken: () => Promise<string>;
  acquireTokenForGraph: () => Promise<string>;
  acquireTokenForOpenAI: () => Promise<string>;
  forceLogin: () => Promise<string>;
}

type AuthContextValue = AuthState & AuthActions;

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { isLoading: configLoading, errors: configErrors } = useConfig();
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    userName: null,
    userEmail: null,
    error: null,
  });

  useEffect(() => {
    if (configLoading || configErrors.length > 0) return;
    (async () => {
      try {
        await initializeMsal();
        const authenticated = checkAuth();
        setState({
          isAuthenticated: authenticated,
          isLoading: false,
          userName: authenticated ? getUserName() : null,
          userEmail: authenticated ? getUserEmail() : null,
          error: null,
        });
      } catch (err: any) {
        setState({
          isAuthenticated: false,
          isLoading: false,
          userName: null,
          userEmail: null,
          error: err?.message || "Failed to initialize authentication",
        });
      }
    })();
  }, [configLoading, configErrors]);

  const login = useCallback(async () => {
    setState((s) => ({ ...s, isLoading: true, error: null }));
    try {
      await msalLogin();
      setState({
        isAuthenticated: true,
        isLoading: false,
        userName: getUserName(),
        userEmail: getUserEmail(),
        error: null,
      });
    } catch (err: any) {
      setState((s) => ({
        ...s,
        isLoading: false,
        error: "Login failed. Please try again.",
      }));
    }
  }, []);

  const logout = useCallback(async () => {
    await msalLogout();
    setState({
      isAuthenticated: false,
      isLoading: false,
      userName: null,
      userEmail: null,
      error: null,
    });
  }, []);

  const acquireToken = useCallback(() => msalAcquireToken(), []);
  const acquireTokenForGraph = useCallback(
    () => msalAcquireTokenForGraph(),
    [],
  );
  const acquireTokenForOpenAI = useCallback(
    () => msalAcquireTokenForOpenAI(),
    [],
  );
  const forceLogin = useCallback(async () => {
    const token = await msalForceLogin();
    setState({
      isAuthenticated: true,
      isLoading: false,
      userName: getUserName(),
      userEmail: getUserEmail(),
      error: null,
    });
    return token;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        acquireToken,
        acquireTokenForGraph,
        acquireTokenForOpenAI,
        forceLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
